import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Download, FileText, Trash2, Plus, Loader2, ChevronDown, Sparkles, Share2, Check, ImageIcon, Library, Copy, Users } from 'lucide-react';

// Use relative URLs so production frontend always calls production backend
const API_URL = '';

const getCurrentUser = () => {
  try {
    const u = localStorage.getItem('currentUser');
    return u ? JSON.parse(u) : null;
  } catch { return null; }
};

const YEAR_OPTIONS = [1, 2];

export default function StoryGeneratorPage() {
  const user = getCurrentUser();
  const [stories, setStories] = useState([]);
  const [libraryStories, setLibraryStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedStory, setSelectedStory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [themes, setThemes] = useState([]);
  const [activeTab, setActiveTab] = useState('my'); // 'my' or 'library'
  const [copying, setCopying] = useState(null);

  const [yearLevel, setYearLevel] = useState(1);
  const [selectedTheme, setSelectedTheme] = useState('');
  const [customTheme, setCustomTheme] = useState('');
  const [withImages, setWithImages] = useState(true);

  const loadStories = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/stories/list?teacher_id=${user.id}`);
      if (res.ok) setStories(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [user?.id]);

  const loadLibrary = useCallback(async () => {
    if (!user?.school_id) return;
    try {
      const res = await fetch(`${API_URL}/api/stories/library?school_id=${user.school_id}`);
      if (res.ok) setLibraryStories(await res.json());
    } catch (e) { console.error(e); }
  }, [user?.school_id]);

  const loadThemes = useCallback(async (yl) => {
    try {
      const res = await fetch(`${API_URL}/api/stories/themes/${yl}`);
      if (res.ok) {
        const data = await res.json();
        setThemes(data.themes || []);
        if (data.themes?.length) setSelectedTheme(data.themes[0]);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadStories(); loadLibrary(); }, [loadStories, loadLibrary]);
  useEffect(() => { loadThemes(yearLevel); }, [yearLevel, loadThemes]);

  const handleGenerate = async () => {
    const topic = customTheme.trim() || selectedTheme;
    if (!topic) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/stories/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          year_level: yearLevel,
          theme: selectedTheme,
          custom_theme: customTheme.trim() || null,
          with_images: withImages,
          teacher_id: user.id,
          school_id: user.school_id || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Request failed' }));
        throw new Error(err.detail || 'Generation failed');
      }
      const { job_id } = await res.json();

      let attempts = 0;
      while (attempts < 120) {
        await new Promise(r => setTimeout(r, 2000));
        const statusRes = await fetch(`${API_URL}/api/stories/generate/status/${job_id}`);
        const status = await statusRes.json();

        if (status.status === 'complete') {
          const storyRes = await fetch(`${API_URL}/api/stories/${status.story_id}`);
          if (storyRes.ok) {
            const data = await storyRes.json();
            setSelectedStory(data);
          }
          setShowForm(false);
          setCustomTheme('');
          loadStories();
          break;
        } else if (status.status === 'error') {
          throw new Error(status.error || 'Generation failed');
        }
        attempts++;
      }
      if (attempts >= 120) throw new Error('Generation timed out');
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setGenerating(false);
  };

  const handleDelete = async (storyId) => {
    if (!window.confirm('Delete this story?')) return;
    try {
      await fetch(`${API_URL}/api/stories/${storyId}`, { method: 'DELETE' });
      if (selectedStory?.id === storyId) setSelectedStory(null);
      loadStories();
    } catch (e) { console.error(e); }
  };

  const handleShare = async (storyId) => {
    try {
      const res = await fetch(`${API_URL}/api/stories/${storyId}/share`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setStories(prev => prev.map(s => s.id === storyId ? { ...s, shared: data.shared } : s));
        if (selectedStory?.id === storyId) setSelectedStory(prev => ({ ...prev, shared: data.shared }));
      }
    } catch (e) { console.error(e); }
  };

  const handleLibraryToggle = async (storyId) => {
    try {
      const res = await fetch(`${API_URL}/api/stories/library/toggle/${storyId}`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setStories(prev => prev.map(s => s.id === storyId ? { ...s, shared_to_library: data.shared_to_library } : s));
        if (selectedStory?.id === storyId) setSelectedStory(prev => ({ ...prev, shared_to_library: data.shared_to_library }));
        loadLibrary();
      }
    } catch (e) { console.error(e); }
  };

  const handleCopyToMine = async (storyId) => {
    setCopying(storyId);
    try {
      const res = await fetch(`${API_URL}/api/stories/library/copy/${storyId}?teacher_id=${user.id}&school_id=${user.school_id || ''}`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        loadStories();
        setActiveTab('my');
        alert(`"${data.title}" copied to your stories!`);
      }
    } catch (e) { console.error(e); }
    setCopying(null);
  };

  const viewStory = async (storyId) => {
    try {
      const res = await fetch(`${API_URL}/api/stories/${storyId}`);
      if (res.ok) {
        const data = await res.json();
        const listStory = stories.find(s => s.id === storyId);
        if (listStory) {
          data.shared = listStory.shared;
          data.shared_to_library = listStory.shared_to_library;
        }
        setSelectedStory(data);
      }
    } catch (e) { console.error(e); }
  };

  const viewLibraryStory = async (storyId) => {
    try {
      const res = await fetch(`${API_URL}/api/stories/${storyId}`);
      if (res.ok) {
        const data = await res.json();
        data._isLibrary = true;
        setSelectedStory(data);
      }
    } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="story-generator-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-fuchsia-500 px-6 py-5 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/15 backdrop-blur-sm p-2.5 rounded-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white tracking-tight" data-testid="story-title">
                Story Generator
              </h1>
              <p className="text-violet-100 text-sm">Create illustrated stories for classroom activities</p>
            </div>
          </div>
          <button onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 bg-white text-violet-700 font-semibold rounded-lg px-5 py-2.5 text-sm shadow-sm hover:bg-violet-50 transition-all"
            data-testid="create-story-btn">
            <Plus className="w-4 h-4" /> New Story
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Generation Form */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6" data-testid="generate-form">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              Generate a New Story
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year Level</label>
                <div className="relative">
                  <select value={yearLevel} onChange={e => setYearLevel(parseInt(e.target.value))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white appearance-none pr-8 focus:ring-2 focus:ring-violet-500"
                    data-testid="year-select">
                    {YEAR_OPTIONS.map(y => <option key={y} value={y}>Year {y}</option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2.5 top-3 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Theme (Curriculum)</label>
                <div className="relative">
                  <select value={selectedTheme} onChange={e => setSelectedTheme(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white appearance-none pr-8 focus:ring-2 focus:ring-violet-500"
                    data-testid="theme-select">
                    {themes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2.5 top-3 pointer-events-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Or Custom Theme</label>
                <input value={customTheme} onChange={e => setCustomTheme(e.target.value)}
                  placeholder="e.g., A boy who finds a turtle at the beach"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-violet-500"
                  data-testid="custom-theme-input" />
              </div>
            </div>
            <div className="flex items-center gap-6 mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={withImages} onChange={e => setWithImages(e.target.checked)}
                  className="rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                  data-testid="with-images-checkbox" />
                <span className="text-sm text-gray-700 flex items-center gap-1">
                  <ImageIcon className="w-4 h-4" /> Include AI illustrations
                </span>
              </label>
            </div>
            <button onClick={handleGenerate} disabled={generating || (!customTheme.trim() && !selectedTheme)}
              className="inline-flex items-center gap-2 bg-violet-600 text-white font-semibold rounded-lg px-6 py-2.5 text-sm hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
              data-testid="generate-btn">
              {generating ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Generating story (1-2 min)...</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Generate Story</>
              )}
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Stories List / Library */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Tab Switcher */}
              <div className="flex border-b border-gray-100">
                <button
                  onClick={() => setActiveTab('my')}
                  className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${
                    activeTab === 'my'
                      ? 'text-violet-700 border-b-2 border-violet-600 bg-violet-50/50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid="tab-my-stories">
                  <BookOpen className="w-3.5 h-3.5 inline mr-1.5" />
                  My Stories ({stories.length})
                </button>
                <button
                  onClick={() => { setActiveTab('library'); loadLibrary(); }}
                  className={`flex-1 px-4 py-3 text-sm font-semibold transition-colors ${
                    activeTab === 'library'
                      ? 'text-violet-700 border-b-2 border-violet-600 bg-violet-50/50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                  data-testid="tab-library">
                  <Library className="w-3.5 h-3.5 inline mr-1.5" />
                  School Library ({libraryStories.length})
                </button>
              </div>

              <div className="divide-y divide-gray-50 max-h-[calc(100vh-340px)] overflow-y-auto">
                {activeTab === 'my' ? (
                  /* My Stories Tab */
                  loading ? (
                    <div className="p-8 text-center text-gray-400">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                    </div>
                  ) : stories.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                      <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No stories yet</p>
                      <p className="text-xs mt-1">Click "New Story" to create one</p>
                    </div>
                  ) : stories.map(story => (
                    <div key={story.id} onClick={() => viewStory(story.id)}
                      className={`px-4 py-3 cursor-pointer hover:bg-violet-50/50 transition-colors ${selectedStory?.id === story.id && !selectedStory?._isLibrary ? 'bg-violet-50 border-l-3 border-violet-600' : ''}`}
                      data-testid={`story-item-${story.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-sm font-medium text-gray-900 truncate">{story.title}</p>
                            {story.shared && <span className="shrink-0 bg-violet-100 text-violet-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">Shared</span>}
                            {story.shared_to_library && <span className="shrink-0 bg-amber-100 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">In Library</span>}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Year {story.year_level} — {story.theme}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {new Date(story.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        </div>
                        <button onClick={e => { e.stopPropagation(); handleDelete(story.id); }}
                          className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"
                          data-testid={`delete-story-${story.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  /* Library Tab */
                  libraryStories.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                      <Library className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No shared stories yet</p>
                      <p className="text-xs mt-1">Share your stories to the school library!</p>
                    </div>
                  ) : libraryStories.map(story => (
                    <div key={story.id} onClick={() => viewLibraryStory(story.id)}
                      className={`px-4 py-3 cursor-pointer hover:bg-amber-50/50 transition-colors ${selectedStory?.id === story.id && selectedStory?._isLibrary ? 'bg-amber-50 border-l-3 border-amber-500' : ''}`}
                      data-testid={`library-item-${story.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{story.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Year {story.year_level} — {story.theme}
                          </p>
                          <p className="text-xs text-amber-600 mt-0.5 flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {story.teacher_name}
                          </p>
                        </div>
                        <button
                          onClick={e => { e.stopPropagation(); handleCopyToMine(story.id); }}
                          disabled={copying === story.id}
                          className="p-1.5 text-amber-500 hover:text-violet-600 transition-colors disabled:opacity-50"
                          title="Copy to My Stories"
                          data-testid={`copy-story-${story.id}`}>
                          {copying === story.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Story Preview */}
          <div className="lg:col-span-2">
            {selectedStory ? (
              <StoryPreview
                story={selectedStory}
                onShare={handleShare}
                onLibraryToggle={handleLibraryToggle}
                onCopy={selectedStory._isLibrary ? () => handleCopyToMine(selectedStory.id) : null}
                isMine={!selectedStory._isLibrary}
                copying={copying === selectedStory?.id}
              />
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-500">Select a story to preview</h3>
                <p className="text-sm text-gray-400 mt-1">Or generate a new illustrated story</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StoryPreview({ story, onShare, onLibraryToggle, onCopy, isMine, copying }) {
  const content = story.content || {};
  const images = story.images || {};
  const pages = content.pages || [];
  const vocab = content.vocabulary || [];
  const questions = content.comprehension_questions || [];
  const colouring = content.colouring_activities || [];
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => setCurrentPage(0), [story.id]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid="story-preview">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-50 to-fuchsia-50 px-6 py-4 border-b border-violet-100">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{content.title || story.title}</h2>
            <p className="text-sm text-gray-500 mt-1">Year {story.year_level} Story Activity</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isMine && (
              <>
                <button onClick={() => onLibraryToggle?.(story.id)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors shadow-sm ${
                    story.shared_to_library ? 'bg-amber-100 text-amber-700 border border-amber-200' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-amber-50'
                  }`} data-testid="library-toggle-btn">
                  <Library className="w-3.5 h-3.5" />
                  {story.shared_to_library ? 'In Library' : 'Add to Library'}
                </button>
                <button onClick={() => onShare?.(story.id)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors shadow-sm ${
                    story.shared ? 'bg-violet-100 text-violet-700 border border-violet-200' : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                  }`} data-testid="share-btn">
                  {story.shared ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
                  {story.shared ? 'Shared' : 'Share'}
                </button>
              </>
            )}
            {onCopy && (
              <button onClick={onCopy} disabled={copying}
                className="inline-flex items-center gap-1.5 bg-amber-500 text-white rounded-lg px-3.5 py-2 text-sm font-medium hover:bg-amber-600 transition-colors shadow-sm disabled:opacity-50"
                data-testid="copy-to-mine-btn">
                {copying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                Copy to My Stories
              </button>
            )}
            <a href={`${API_URL}/api/stories/${story.id}/pptx`}
               className="inline-flex items-center gap-1.5 bg-amber-500 text-white rounded-lg px-3.5 py-2 text-sm font-medium hover:bg-amber-600 transition-colors shadow-sm"
               data-testid="download-pptx-btn">
              <Download className="w-3.5 h-3.5" /> PPT
            </a>
            <a href={`${API_URL}/api/stories/${story.id}/pdf`}
               className="inline-flex items-center gap-1.5 bg-violet-600 text-white rounded-lg px-3.5 py-2 text-sm font-medium hover:bg-violet-700 transition-colors shadow-sm"
               data-testid="download-pdf-btn">
              <Download className="w-3.5 h-3.5" /> PDF
            </a>
            <a href={`${API_URL}/api/stories/${story.id}/docx`}
               className="inline-flex items-center gap-1.5 bg-white text-gray-700 rounded-lg px-3.5 py-2 text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors"
               data-testid="download-word-btn">
              <FileText className="w-3.5 h-3.5" /> Word
            </a>
          </div>
        </div>
      </div>

      {/* Story Book View */}
      <div className="px-6 py-5 max-h-[calc(100vh-320px)] overflow-y-auto">
        {/* Page navigator */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {pages.map((_, i) => (
            <button key={i} onClick={() => setCurrentPage(i)}
              className={`w-8 h-8 rounded-full text-sm font-bold transition-all ${
                i === currentPage ? 'bg-violet-600 text-white shadow-md' : 'bg-gray-100 text-gray-500 hover:bg-violet-100'
              }`} data-testid={`page-btn-${i}`}>
              {i + 1}
            </button>
          ))}
          <button onClick={() => setCurrentPage(pages.length)}
            className={`px-3 h-8 rounded-full text-xs font-bold transition-all ${
              currentPage === pages.length ? 'bg-violet-600 text-white shadow-md' : 'bg-gray-100 text-gray-500 hover:bg-violet-100'
            }`}>Activities</button>
        </div>

        {/* Story page */}
        {currentPage < pages.length ? (
          <div className="text-center" data-testid={`story-page-${currentPage}`}>
            {images[String(pages[currentPage]?.page_number)] && (
              <div className="mb-4 bg-gray-50 rounded-xl p-4 border border-gray-100 inline-block">
                <img
                  src={`data:image/png;base64,${images[String(pages[currentPage].page_number)]}`}
                  alt={`Page ${currentPage + 1}`}
                  className="max-w-full h-auto rounded-lg shadow-sm mx-auto"
                  style={{ maxHeight: '380px' }}
                  data-testid={`story-image-${currentPage}`}
                />
              </div>
            )}
            <p className="text-xl leading-relaxed text-gray-800 font-medium max-w-lg mx-auto mt-4">
              {pages[currentPage]?.text}
            </p>
            <div className="flex justify-center gap-4 mt-6">
              <button onClick={() => setCurrentPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium disabled:opacity-30 hover:bg-gray-200 transition-colors">
                Previous
              </button>
              <button onClick={() => setCurrentPage(currentPage + 1)}
                className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-colors">
                {currentPage === pages.length - 1 ? 'Activities' : 'Next Page'}
              </button>
            </div>
          </div>
        ) : (
          /* Activities section */
          <div data-testid="activities-section">
            {vocab.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold text-violet-800 mb-3 pb-2 border-b border-violet-100">Vocabulary Words</h3>
                <div className="grid grid-cols-2 gap-2">
                  {vocab.map((v, i) => (
                    <div key={i} className="bg-violet-50 rounded-lg px-3 py-2 border border-violet-100">
                      <span className="font-bold text-violet-700 text-sm">{v.word}</span>
                      <span className="text-gray-600 text-xs ml-1">— {v.meaning}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {questions.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold text-violet-800 mb-3 pb-2 border-b border-violet-100">Comprehension Questions</h3>
                <div className="space-y-3">
                  {questions.map((q, j) => (
                    <div key={j} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        <span className="text-violet-600 font-bold">Q{j + 1}.</span> {q.question}
                      </p>
                      <details className="mt-1">
                        <summary className="text-xs text-violet-600 cursor-pointer font-medium">Show Answer</summary>
                        <p className="text-sm text-emerald-700 mt-1 pl-3 border-l-2 border-emerald-200">{q.answer}</p>
                      </details>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {colouring.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold text-violet-800 mb-3 pb-2 border-b border-violet-100">Colouring & Drawing Activities</h3>
                <div className="space-y-2">
                  {colouring.map((act, k) => (
                    <div key={k} className="flex items-start gap-2 bg-fuchsia-50 rounded-lg px-3 py-2.5 border border-fuchsia-100">
                      <span className="text-fuchsia-600 font-bold text-sm">{k + 1}.</span>
                      <span className="text-sm text-gray-700">{act}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button onClick={() => setCurrentPage(0)}
              className="mt-4 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-colors">
              Back to Story
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
